import { Button, Input, Space, message } from "antd";
import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { useEffect, useMemo, useState } from "react";

import { LOG_DATE_FORMAT_END, LOG_DATE_FORMAT_START } from "@/config";
import { scheduleService } from "@/api/services/scheduleService";
import { confirmDialog } from "@/utils/confirmDialog";
import { showRequestError } from "@/utils/requestErrorMessage";
import type { OrderVo, ScheduleDateVo, ScheduleForm, ScheduleVo } from "@zhihaoscm/entity";
import { dateFormat, fmtNum } from "@zhihaoscm/utils";
import {
    DELIVERY_STATE,
    ORDER_TYPE,
    RECEIVE_STATE,
    SCHEDULE_STATE,
    SETTLEMENT_METHOD,
    TRADE_ORDER_STATE
} from "@zhihaoscm/meta";
import { Dialog as ZhDialog, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";
import { areaLib } from "@/utils/areaLib";
import { getShipInfoName } from "./tradeOrder/orderManage";
import { WAYBILL_STATE } from "@/meta/logistics/waybill";

interface SchedulePageState {
    page: number;
    size: number;
    total: number;
}

interface SchedulePageResult {
    list?: ScheduleVo[];
    total?: number;
}

const DEFAULT_PAGE_SIZE = 5;
/** 旧 Vue 的排期日历按订单创建日与提货开始日的较晚日期作为查询起点。 */
export function getTradeOrderScheduleQuery(order: OrderVo["order"]) {
    const createdDay = order.createdTime ? dateFormat(order.createdTime) : "";
    const deliveryStartDay = order.deliveryStartTime ? dateFormat(order.deliveryStartTime) : "";
    const beginDay =
        createdDay && deliveryStartDay && new Date(createdDay).getTime() >= new Date(deliveryStartDay).getTime()
            ? createdDay
            : deliveryStartDay;

    if (!order.id || !beginDay || !order.deliveryEndTime) return null;
    return {
        orderId: String(order.id),
        beginTime: dateFormat(beginDay, LOG_DATE_FORMAT_START),
        endTime: dateFormat(order.deliveryEndTime, LOG_DATE_FORMAT_END)
    };
}

function formatTon(value: number | string | undefined): string {
    return fmtNum(value);
}

function formatScheduleAddress(row: ScheduleVo): string {
    const address = row.schedule?.addressInfo;
    if (!address) return "--";
    const areaName = areaLib.getFullAreaName(address.province, address.city, address.district);
    return [address.contacts, address.cellphone, areaName, address.detailAddress].filter(Boolean).join("") || "--";
}

function formatShipName(row: ScheduleVo): string {
    return getShipInfoName(row.schedule?.shipInfo, row.schedule?.shipId) || "--";
}

/** 完整复刻 Vue scheduleList 的新旧数据操作规则，避免所有排期都错误显示修改/删除。 */
function getScheduleActions(row: ScheduleVo, order: OrderVo["order"]) {
    const state = Number(row.schedule?.state);
    const orderType = Number(order.orderType);
    const isNewData = Number(row.schedule?.newDataFlag) === 1;
    const orderCanOperate = ![TRADE_ORDER_STATE.COMPLETED.code, TRADE_ORDER_STATE.CANCELLED.code].includes(
        Number(order.state)
    );
    if (
        isNewData &&
        [ORDER_TYPE.GROUP_BUYING_PRODUCTS.code, ORDER_TYPE.CONSIGNMENT_PRODUCTS.code].includes(orderType)
    ) {
        const pendingGeneration = state === SCHEDULE_STATE.TO_BE_GENERATED.code;
        return { edit: orderCanOperate && pendingGeneration, delete: orderCanOperate && pendingGeneration };
    }
    const edit =
        (orderCanOperate || orderType === ORDER_TYPE.PICKING_UP_MISSING_PRODUCTS.code) &&
        ![SCHEDULE_STATE.CANCELED.code, SCHEDULE_STATE.PICKED_UP.code].includes(state);
    let deletable = false;
    if (isNewData) {
        deletable = state === SCHEDULE_STATE.TO_BE_GENERATED.code;
    } else if (Number(order.productInfo?.settlementMethod) === SETTLEMENT_METHOD.FULL_PAYMENT.code) {
        deletable = true;
    } else if (orderType === ORDER_TYPE.PICKING_UP_MISSING_PRODUCTS.code) {
        deletable = state === SCHEDULE_STATE.TO_BE_CONFIRMED.code;
    } else {
        deletable = Boolean(
            row.receivePayments?.some(
                payment => payment.scheduleId === row.schedule?.id && payment.state === RECEIVE_STATE.UNPAID.code
            )
        );
    }
    return { edit, delete: edit && deletable };
}

function scheduleColumns(
    onEdit: (row: ScheduleVo) => void,
    onDelete: (row: ScheduleVo) => void,
    order: OrderVo["order"]
): TableColumnProps<ScheduleVo>[] {
    return [
        { key: "id", label: "排期编号", prop: "schedule.id", render: ({ row }) => row.schedule?.id || "--" },
        {
            key: "deliveryId",
            label: "关联提货",
            prop: "delivery.id",
            render: ({ row }) => row.delivery?.id || "--"
        },
        {
            key: "scheduleDate",
            label: "排期日期",
            prop: "schedule.scheduleDate",
            render: ({ row }) => dateFormat(row.schedule?.scheduleDate) || "--"
        },
        {
            key: "estimatedTon",
            label: "预估提货吨数（吨）",
            prop: "schedule.estimatedTon",
            render: ({ row }) => formatTon(row.schedule?.estimatedTon)
        },
        { key: "shipName", label: "船舶", prop: "shipName", render: ({ row }) => formatShipName(row) },
        {
            key: "transportOrderShipId",
            label: "关联运单",
            prop: "transportOrderShipId",
            render: ({ row }) =>
                row.transportOrderShip?.id
                    ? `${row.transportOrderShip.id}（${WAYBILL_STATE.getIntroByCode(Number(row.transportOrderShip.state)) || "--"}）`
                    : "--"
        },
        { key: "address", label: "收货信息", prop: "address", render: ({ row }) => formatScheduleAddress(row) },
        {
            key: "state",
            label: "排期状态",
            prop: "schedule.state",
            render: ({ row }) => SCHEDULE_STATE.getIntroByCode(Number(row.schedule?.state)) || "--"
        },
        {
            key: "operation",
            label: "操作",
            prop: "operation",
            width: 120,
            render: ({ row }) => {
                const actions = getScheduleActions(row, order);
                return (
                    <>
                        {actions.edit ? (
                            <Button type="link" onClick={() => onEdit(row)}>
                                修改
                            </Button>
                        ) : null}
                        {actions.delete ? (
                            <Button type="link" onClick={() => onDelete(row)}>
                                删除
                            </Button>
                        ) : null}
                    </>
                );
            }
        }
    ];
}

export interface TradeOrderSchedulePanelProps {
    detail: OrderVo;
}

/** 交易订单排期信息，保留旧 Vue 的指标、日期查询和五条分页规则。 */
export function TradeOrderSchedulePanel({ detail }: TradeOrderSchedulePanelProps) {
    const [scheduleDates, setScheduleDates] = useState<ScheduleDateVo[]>([]);
    const [scheduleRows, setScheduleRows] = useState<ScheduleVo[]>([]);
    const [calendarPage, setCalendarPage] = useState(0);
    const [pageState, setPageState] = useState<SchedulePageState>({
        page: 1,
        size: DEFAULT_PAGE_SIZE,
        total: 0
    });
    const [editingSchedule, setEditingSchedule] = useState<ScheduleVo | null>(null);
    const [editingScheduleDate, setEditingScheduleDate] = useState("");
    const [scheduleSubmitting, setScheduleSubmitting] = useState(false);
    const order = detail.order;
    const query = useMemo(() => getTradeOrderScheduleQuery(order), [order]);

    useEffect(() => {
        let disposed = false;
        setScheduleDates([]);
        setScheduleRows([]);
        setCalendarPage(0);
        setPageState({ page: 1, size: DEFAULT_PAGE_SIZE, total: 0 });
        if (!query) return;

        // 旧 Vue 分别请求日历分组与排期明细分页，保留各自 API payload。
        void Promise.all([
            scheduleService.scheduleDate(query),
            scheduleService.schedulePaging({
                page: 1,
                size: DEFAULT_PAGE_SIZE,
                orderId: query.orderId
            }) as unknown as Promise<SchedulePageResult>
        ])
            .then(([dateResult, pageResult]) => {
                if (disposed) return;
                setScheduleDates(dateResult || []);
                setScheduleRows(pageResult?.list || []);
                setPageState({ page: 1, size: DEFAULT_PAGE_SIZE, total: pageResult?.total || 0 });
            })
            .catch(() => {
                if (disposed) return;
                setScheduleDates([]);
                setScheduleRows([]);
            });
        return () => {
            disposed = true;
        };
    }, [query]);

    const handlePageChange = (page: number, pageSize: number) => {
        if (!query) return;
        const nextPage = pageSize !== pageState.size ? 1 : page;
        setPageState(current => ({ ...current, page: nextPage, size: pageSize }));
        const schedulePromise = scheduleService.schedulePaging({
            page: nextPage,
            size: pageSize,
            orderId: query.orderId
        }) as unknown as Promise<SchedulePageResult>;
        void schedulePromise
            .then(result => {
                setScheduleRows(result?.list || []);
                setPageState(current => ({
                    ...current,
                    page: nextPage,
                    size: pageSize,
                    total: result?.total || current.total
                }));
            })
            .catch(() => setScheduleRows([]));
    };

    /** 重新读取排期列表，确保新增、修改或删除后当前子页面立即同步。 */
    const reloadScheduleRows = async () => {
        if (!query) return;
        try {
            const result = (await scheduleService.schedulePaging({
                page: pageState.page,
                size: pageState.size,
                orderId: query.orderId
            })) as unknown as SchedulePageResult;
            setScheduleRows(result?.list || []);
            setPageState(current => ({ ...current, total: result?.total || current.total }));
        } catch (error) {
            showRequestError(error, "排期列表加载失败");
        }
    };

    /** 打开旧 Vue 排期编辑入口并回显排期日期。 */
    const openEditSchedule = (row: ScheduleVo) => {
        setEditingSchedule(row);
        setEditingScheduleDate(row.schedule?.scheduleDate?.slice(0, 10) || "");
    };

    /** 提交排期日期修改，复用 Vue ScheduleForm 的完整关联字段。 */
    const submitEditSchedule = async () => {
        const schedule = editingSchedule?.schedule;
        if (!schedule?.id || !editingScheduleDate) {
            message.error("请选择排期日期");
            return;
        }
        setScheduleSubmitting(true);
        try {
            const form: ScheduleForm = {
                id: String(schedule.id),
                scheduleDate: `${editingScheduleDate}T00:00:00`,
                productId: String(schedule.productId),
                orderId: String(schedule.orderId),
                estimatedTon: Number(schedule.estimatedTon),
                transportOrderShipId: String(schedule.transportOrderShipId || ""),
                shipId: String(schedule.shipId || ""),
                addressInfo: schedule.addressInfo
            };
            await scheduleService.update(String(schedule.id), form);
            message.success("修改排期成功");
            setEditingSchedule(null);
            await reloadScheduleRows();
        } catch (error) {
            showRequestError(error, "修改排期失败");
        } finally {
            setScheduleSubmitting(false);
        }
    };

    /** 删除排期并刷新当前排期子列表，保持 Vue 的确认和刷新行为。 */
    const deleteSchedule = async (row: ScheduleVo) => {
        const id = row.schedule?.id;
        if (!id || !(await confirmDialog("确定要删除排期吗？"))) return;
        try {
            await scheduleService.delete(String(id));
            message.success("删除排期成功");
            await reloadScheduleRows();
        } catch (error) {
            showRequestError(error, "删除排期失败");
        }
    };

    const remainingLabel = Number(order.orderType) === 3 ? "剩余提货吨数（吨）" : "预估剩余提货吨数（吨）";
    const remainingTon =
        detail.remainPickedUpTon === undefined || detail.remainPickedUpTon === null
            ? "--"
            : Number(detail.remainPickedUpTon) <= 0
              ? "0"
              : formatTon(detail.remainPickedUpTon);
    const calendarPageSize = 15;
    const calendarPageCount = Math.max(1, Math.ceil(scheduleDates.length / calendarPageSize));
    const visibleScheduleDates = scheduleDates.slice(
        calendarPage * calendarPageSize,
        (calendarPage + 1) * calendarPageSize
    );

    /** 根据 Vue caclenderCard/cardIdText/cardTonText 组合出单个排期事件。 */
    const renderScheduleEvent = (row: ScheduleVo, index: number, isPastDay: boolean) => {
        const schedule = row.schedule;
        const isNewData = Number(schedule?.newDataFlag) === 1;
        const isPickingOrder = Number(schedule?.productType) === ORDER_TYPE.PICKING_UP_MISSING_PRODUCTS.code;
        const hasDelivery = Boolean(row.delivery);
        const showDeliveryId =
            !isPickingOrder && isNewData
                ? hasDelivery
                : isPastDay && Number(schedule?.state) === SCHEDULE_STATE.PICKED_UP.code;
        const identifierLabel = showDeliveryId ? "提货编号" : "排期编号";
        const identifier = showDeliveryId ? row.delivery?.id : schedule?.id;
        const stateLabel = row.delivery?.state
            ? DELIVERY_STATE.getIntroByCode(Number(row.delivery.state))
            : SCHEDULE_STATE.getIntroByCode(Number(schedule?.state));
        const shipName = getShipInfoName(schedule?.shipInfo, schedule?.shipId) || "";
        const showPickedUpTon =
            isPastDay && Number(schedule?.state) === SCHEDULE_STATE.PICKED_UP.code && row.delivery?.estimatedTon;
        const tonLabel = !isPickingOrder && isNewData && hasDelivery ? "提货吨数" : "预估提货";
        const tonValue = hasDelivery && isNewData ? row.delivery?.estimatedTon : schedule?.estimatedTon;
        return (
            <div className="trade-order-schedule-calendar__event" key={String(schedule?.id || index)}>
                <div className="trade-order-schedule-calendar__event-header">
                    <span className="trade-order-schedule-calendar__event-index">{index + 1}</span>
                    <strong>{stateLabel || "--"}</strong>
                </div>
                <div className="trade-order-schedule-calendar__event-details">
                    <span>{identifierLabel}</span>
                    <b>{identifier || "--"}</b>
                    {shipName ? <span>船舶</span> : null}
                    {shipName ? <b>{shipName}</b> : null}
                    {showPickedUpTon ? <span>提货吨数</span> : tonValue ? <span>{tonLabel}</span> : null}
                    {showPickedUpTon ? (
                        <b>{formatTon(row.delivery?.estimatedTon)}吨</b>
                    ) : tonValue ? (
                        <b>{formatTon(tonValue)}吨</b>
                    ) : null}
                </div>
            </div>
        );
    };

    // 旧版先按日期判断过期，再决定展示“预估可提/剩余可提”，排期明细始终保留在卡片内。
    const renderScheduleDate = (item: ScheduleDateVo) => {
        const dateValue = new Date(item.scheduleDate);
        const weekday = dateValue.toLocaleDateString("zh-CN", { weekday: "short" });
        const hasSchedule = Boolean(item.scheduleVos?.length);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const isPastDay = dateValue.getTime() < today.getTime();
        const availableTon = Math.max(Number(item.remainAvailablePickedUpTon || 0), 0);
        const statusLabel = isPastDay ? "不可提货" : hasSchedule ? "剩余可提" : "预估可提";
        return (
            <div
                className={`trade-order-schedule-calendar__cell${isPastDay ? " is-expired" : ""}`}
                key={item.scheduleDate}
            >
                <div className="trade-order-schedule-calendar__date">
                    <div className="trade-order-schedule-calendar__date-left">
                        <span>{dateFormat(item.scheduleDate)}</span>
                        <span className="trade-order-schedule-calendar__weekday">{weekday}</span>
                    </div>
                </div>
                <div
                    className={`trade-order-schedule-calendar__status ${!isPastDay && availableTon > 0 ? "is-available" : "is-unavailable"}`}
                >
                    <span className="trade-order-schedule-calendar__dot">●</span>
                    {statusLabel}
                    {!isPastDay ? (
                        <strong>
                            {formatTon(availableTon)}
                            <span className="trade-order-schedule-calendar__unit">吨</span>
                        </strong>
                    ) : null}
                </div>
                <div className="trade-order-schedule-calendar__events">
                    {(item.scheduleVos || []).map((row, index) => renderScheduleEvent(row, index, isPastDay))}
                </div>
            </div>
        );
    };

    return (
        <section className="section-card mt-4 trade-order-schedule-panel" data-testid="trade-order-schedule-panel">
            <div className="trade-order-schedule-panel__header">
                <div className="section-title mb-0">排期提货日历</div>
                <div className="trade-order-schedule-panel__month-actions">
                    <Button
                        type="primary"
                        ghost
                        size="middle"
                        disabled={calendarPage <= 0}
                        onClick={() => setCalendarPage(page => Math.max(0, page - 1))}
                    >
                        <i aria-hidden="true" className="iconfont icon-arrow-left" />
                        上一页
                    </Button>
                    <Button
                        type="primary"
                        ghost
                        size="middle"
                        disabled={calendarPage >= calendarPageCount - 1}
                        onClick={() => setCalendarPage(page => Math.min(calendarPageCount - 1, page + 1))}
                    >
                        下一页
                        <i aria-hidden="true" className="iconfont icon-arrow-rifht" />
                    </Button>
                </div>
            </div>
            <div className="trade-order-schedule-metrics">
                <div>
                    <span>可提货吨数（吨）</span>
                    <strong>{formatTon(order.ton)}</strong>
                </div>
                <div>
                    <span>排期提货吨数（吨）</span>
                    <strong>{formatTon(detail.scheduledDeliveryTon)}</strong>
                </div>
                <div>
                    <span>已提货吨数（吨）</span>
                    <strong>{formatTon(detail.pickedUpTon)}</strong>
                </div>
                <div>
                    <span>{remainingLabel}</span>
                    <strong>{remainingTon}</strong>
                </div>
            </div>
            {scheduleDates.length ? (
                <div className="trade-order-schedule-calendar" data-testid="trade-order-schedule-dates">
                    {visibleScheduleDates.map(renderScheduleDate)}
                </div>
            ) : (
                <div className="trade-order-schedule-calendar__empty">暂无排期数据</div>
            )}
            <div className="trade-order-schedule-list">
                <ZhTable<ScheduleVo>
                    data={scheduleRows}
                    columns={scheduleColumns(openEditSchedule, deleteSchedule, order)}
                    pagination={false}
                    rowKey={row => String(row.schedule?.id)}
                    append={
                        pageState.total > 5 ? (
                            <div className="admin-table-composed-pagination">
                                <ZhPagination
                                    current={pageState.page}
                                    pageSize={pageState.size}
                                    total={pageState.total}
                                    pageSizeOptions={[5, 10, 20, 30, 40, 50, 100]}
                                    onChange={handlePageChange}
                                />
                            </div>
                        ) : null
                    }
                />
            </div>
            <ZhDialog
                open={editingSchedule !== null}
                title="修改排期"
                width="520px"
                onCancel={() => {
                    if (!scheduleSubmitting) setEditingSchedule(null);
                }}
                footer={
                    <Space>
                        <Button disabled={scheduleSubmitting} onClick={() => setEditingSchedule(null)}>
                            取消
                        </Button>
                        <Button loading={scheduleSubmitting} type="primary" onClick={() => void submitEditSchedule()}>
                            确认
                        </Button>
                    </Space>
                }
            >
                <label className="block" htmlFor="trade-order-schedule-date">
                    排期日期
                </label>
                <Input
                    id="trade-order-schedule-date"
                    type="date"
                    value={editingScheduleDate}
                    onChange={event => setEditingScheduleDate(event.target.value)}
                />
            </ZhDialog>
        </section>
    );
}

export default TradeOrderSchedulePanel;
